import React, { useState, useEffect } from 'react';
import { ref, get, update, remove } from 'firebase/database';
import { database } from '../firebase';

const DeviceAdminPanel = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [devices, setDevices] = useState([]);
    const [filteredDevices, setFilteredDevices] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAllDevices();
    }, []);

    const fetchAllDevices = async () => {
        setLoading(true);
        setError('');
        try {
            const devicesRef = ref(database, 'devices');
            const snapshot = await get(devicesRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const devicesList = [];

                // Lógica de aplanado robusta
                Object.entries(data).forEach(([key, value]) => {
                    if (value.deviceId || value.deviceInfo) {
                        // Estructura plana: devices/{deviceId}
                        devicesList.push({
                            deviceId: key,
                            ...value,
                            // Normalizar campos de deviceInfo
                            model: value.deviceInfo?.model || value.deviceModel || value.model || 'N/A',
                            manufacturer: value.deviceInfo?.manufacturer || value.manufacturer || 'N/A',
                            osVersion: value.deviceInfo?.androidVersion || value.osVersion || 'N/A',
                            lastLogin: value.lastAccess || value.lastLogin || null
                        });
                    } else {
                        // Estructura agrupada: devices/{uid}/{deviceId}
                        Object.entries(value).forEach(([dId, dInfo]) => {
                            devicesList.push({
                                deviceId: dId,
                                ...dInfo,
                                model: dInfo.deviceInfo?.model || dInfo.deviceModel || dInfo.model || 'N/A',
                                manufacturer: dInfo.deviceInfo?.manufacturer || dInfo.manufacturer || 'N/A',
                                osVersion: dInfo.deviceInfo?.androidVersion || dInfo.osVersion || 'N/A',
                                lastLogin: dInfo.lastAccess || dInfo.lastLogin || null
                            });
                        });
                    }
                });

                setDevices(devicesList);
                setFilteredDevices(devicesList);
            } else {
                setError('No se encontraron dispositivos en la base de datos.');
            }
        } catch (err) {
            setError('Error al cargar dispositivos: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        if (!searchTerm) {
            setFilteredDevices(devices);
            return;
        }
        const term = searchTerm.toLowerCase();
        const filtered = devices.filter(d =>
            (d.userEmail && d.userEmail.toLowerCase().includes(term)) ||
            (d.userId && d.userId.toLowerCase().includes(term)) ||
            (d.deviceId && d.deviceId.toLowerCase().includes(term)) ||
            (d.model && d.model.toLowerCase().includes(term)) ||
            (d.manufacturer && d.manufacturer.toLowerCase().includes(term))
        );
        setFilteredDevices(filtered);
    };

    const handleAction = async (deviceId, actionType) => {
        if (!window.confirm(`¿Estás seguro de que deseas ${actionType} este dispositivo?`)) return;

        try {
            const targetRef = ref(database, `devices/${deviceId}`);

            if (actionType === 'eliminar') {
                await remove(targetRef);
            } else {
                const snapshot = await get(targetRef);
                if (snapshot.exists()) {
                    const current = snapshot.val();
                    await update(targetRef, { isActive: !current.isActive });
                }
            }

            alert('Acción completada con éxito.');
            fetchAllDevices(); // Recargar datos
        } catch (err) {
            alert('Error: ' + err.message);
        }
    };

    // Agrupar dispositivos por email para la vista
    const groupedDevices = filteredDevices.reduce((acc, device) => {
        const email = device.userEmail || device.email || 'Sin Email';
        if (!acc[email]) acc[email] = [];
        acc[email].push(device);
        return acc;
    }, {});

    return (
        <div className="admin-panel-card">
            <div className="search-section">
                <div className="panel-header">
                    <h3>📱 Gestión de Dispositivos</h3>
                    <button className="btn-refresh" onClick={fetchAllDevices} disabled={loading}>
                        {loading ? 'Cargando...' : '🔄 Refrescar Todo'}
                    </button>
                </div>
                <div className="search-bar">
                    <input
                        type="text"
                        placeholder="Buscar por Email, ID de Usuario o ID de Dispositivo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button onClick={handleSearch} className="btn-save">
                        🔍 Filtrar
                    </button>
                </div>
            </div>

            {error && <p className="error-msg">{error}</p>}

            <div className="device-results">
                {Object.keys(groupedDevices).length === 0 ? (
                    <p className="no-data">No se encontraron dispositivos que coincidan con la búsqueda.</p>
                ) : (
                    Object.entries(groupedDevices).map(([email, userDevices]) => (
                        <div key={email} className="user-device-group">
                            <div className="user-group-header">
                                <strong>📧 {email}</strong>
                                <span className="user-id-label">UID: {userDevices[0].userId || 'N/A'}</span>
                            </div>
                            <div className="devices-grid-mini">
                                {userDevices.map((device) => (
                                    <div key={device.deviceId} className={`device-item ${device.isActive ? 'active-border' : 'inactive-border'}`}>
                                        <div className="device-header">
                                            <span className="device-id-tag">ID: {device.deviceId}</span>
                                            <span className={`status-badge ${device.isActive ? 'active' : 'inactive'}`}>
                                                {device.isActive ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </div>
                                        <div className="device-info-body">
                                            <p><strong>Modelo:</strong> {device.model || device.deviceModel || 'N/A'}</p>
                                            <p><strong>Marca:</strong> {device.manufacturer || 'N/A'}</p>
                                            <p><strong>Android:</strong> {device.osVersion || 'N/A'}</p>
                                            {device.lastLogin && <p className="time-tag">Último acceso: {new Date(device.lastLogin).toLocaleString()}</p>}
                                        </div>
                                        <div className="device-actions">
                                            <button
                                                onClick={() => handleAction(device.deviceId, device.isActive ? 'desactivar' : 'activar')}
                                                className="btn-warn"
                                            >
                                                {device.isActive ? 'Desactivar' : 'Activar'}
                                            </button>
                                            <button onClick={() => handleAction(device.deviceId, 'eliminar')} className="btn-danger">
                                                Eliminar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <style>{`
                .user-device-group { 
                    background: rgba(255, 255, 255, 0.05); 
                    border-radius: 12px; 
                    padding: 20px; 
                    margin-bottom: 25px; 
                    border: 1px solid rgba(255, 255, 255, 0.1);
                }
                .user-group-header { 
                    display: flex; 
                    justify-content: space-between; 
                    align-items: center; 
                    margin-bottom: 15px; 
                    padding-bottom: 10px; 
                    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
                }
                .user-id-label { font-size: 0.8em; color: #888; }
                .devices-grid-mini { 
                    display: grid; 
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); 
                    gap: 15px; 
                }
                .device-id-tag { font-size: 0.75em; color: var(--admin-btn-custom); font-family: monospace; }
                .device-info-body p { margin: 4px 0; font-size: 0.9em; }
                .time-tag { font-size: 0.8em; color: #666; font-style: italic; margin-top: 8px !important; }
                .active-border { border-left: 4px solid #2ecc71; }
                .inactive-border { border-left: 4px solid #95a5a6; }
            `}</style>
        </div>
    );
};

export default DeviceAdminPanel;
